import {useAiChat} from '@/ai-chat/providers/ai-chat.context';
import {useAiChatCallbacks} from '@AiChat/callbacks/ai-chat-callbacks.context';
import {AiChatGeneratedAssetType} from '@AiChat/ai-chat.types';
import type {AiChatAnimation} from '@Components/ai-chat-window/components/assistant-message-image-part-animation-creations/assistant-message-image-part-animation-creations.types';
import {AnimationCreationStatus} from '@Components/ai-chat-window/components/assistant-message-image-part-animation-creations/assistant-message-image-part-animation-creations.types';
import {getChatIdToSaveVideoIn, getVideoCreationStateKey} from '@Components/ai-chat-window/lib/attachment-video-key.library';
import type {AnimationGenerationError} from '@Components/ai-chat-window/lib/ai-chat-animation-generation.library';
import {
  AnimationGenerationErrorCode,
  ensureAnimationCredits,
  generateAnimationForAiChatImage,
  getAnimationErrorMessage,
} from '@Components/ai-chat-window/lib/ai-chat-animation-generation.library';
import {animationRunsInFlight} from '@Components/ai-chat-window/lib/ai-chat-animation-runs.library';
import {markAiChatCreateVideoIntroTileAsSeen} from '@Components/ai-chat-window/components/assistant-message-image-part-action-tiles/assistant-message-image-part-action-tiles.library';
import {AiChatTurnToVideoSource, AiChatTurnToVideoType, GA4EventName, GA4EventParamName} from '@Libraries/ga-events';
import type {UseTurnImageIntoVideo, UseTurnImageIntoVideoParams} from './useTurnImageIntoVideo.types';

export function useTurnImageIntoVideo({chatId, hashedId, videoSubject, canWrite, source = AiChatTurnToVideoSource.DESIGN}: UseTurnImageIntoVideoParams): UseTurnImageIntoVideo {
  const creationStateKey = getVideoCreationStateKey(videoSubject);
  const {state, actions} = useAiChat();
  const callbacks = useAiChatCallbacks();

  const trackTurnToVideo = (generateAudio: boolean): void => {
    let type = AiChatTurnToVideoType.SILENT;

    if (generateAudio) {
      type = AiChatTurnToVideoType.WITH_AUDIO;
    }

    window.PMW.gtm.trackGA4CustomEvent(GA4EventName.AICHAT_TURNTOVIDEO, {
      [GA4EventParamName.SOURCE]: source,
      [GA4EventParamName.TYPE]: type,
    });
  };

  const markCreating = (abortController: AbortController): void => {
    actions.updateActionTileStates({isCreateAVideoFeatureIntroVisible: false});
    void markAiChatCreateVideoIntroTileAsSeen();
    actions.updateAnimationCreationState(chatId, creationStateKey, {
      isVisible: true,
      status: AnimationCreationStatus.CREATING,
      abortController,
    });
  };

  const markCompleted = (animation: AiChatAnimation): void => {
    actions.updateAnimationCreationState(chatId, creationStateKey, {
      status: AnimationCreationStatus.COMPLETED,
      animations: [animation],
      abortController: undefined,
      pollingKey: undefined,
    });
    actions.refreshCredits?.();
    callbacks.onAssetGenerated?.({
      chatId,
      type: AiChatGeneratedAssetType.VIDEO,
      assetHashedFilename: animation.animationHashedId,
      sourceImageHashedId: hashedId,
      durationInSeconds: animation.duration,
      hasAudio: animation.hasAudio,
    });
  };

  const markIdle = (): void => {
    actions.updateAnimationCreationState(chatId, creationStateKey, {
      isVisible: false,
      status: AnimationCreationStatus.IDLE,
      abortController: undefined,
      pollingKey: undefined,
    });
  };

  const markFailed = (code: AnimationGenerationErrorCode | undefined): void => {
    actions.updateAnimationCreationState(chatId, creationStateKey, {
      status: AnimationCreationStatus.FAILED,
      errorMessage: getAnimationErrorMessage(code),
      abortController: undefined,
      pollingKey: undefined,
    });
    actions.refreshCredits?.();
  };

  const generate = async (abortController: AbortController, generateAudio: boolean): Promise<void> => {
    const animation = await generateAnimationForAiChatImage(hashedId, {
      signal: abortController.signal,
      onPollingKeyReceived: (pollingKey: string): void => {
        actions.updateAnimationCreationState(chatId, creationStateKey, {pollingKey});
      },
      generateAudio,
      chatId: getChatIdToSaveVideoIn(chatId, videoSubject),
    });

    markCompleted(animation);
  };

  const run = async (): Promise<void> => {
    const generateAudio = state.aiChatGenerateAudio;
    const hasCredits = await ensureAnimationCredits(generateAudio);

    if (!hasCredits) {
      return;
    }

    trackTurnToVideo(generateAudio);

    const abortController = new AbortController();
    markCreating(abortController);

    try {
      await generate(abortController, generateAudio);
    } catch (e) {
      const code = (e as AnimationGenerationError).code;

      if (code === AnimationGenerationErrorCode.INSUFFICIENT_CREDITS) {
        markIdle();
        const hasPurchasedCredits = await ensureAnimationCredits(generateAudio);

        if (hasPurchasedCredits) {
          await run();
        }

        return;
      }

      if (!abortController.signal.aborted) {
        markFailed(code);
      }
    }
  };

  const turnIntoVideo = (): void => {
    if (!canWrite || animationRunsInFlight.has(chatId, creationStateKey)) {
      return;
    }

    animationRunsInFlight.add(chatId, creationStateKey);
    void run().finally((): void => {
      animationRunsInFlight.remove(chatId, creationStateKey);
    });
  };

  return {
    turnIntoVideo,
    isGeneratingVideo: state.getAnimationCreationState(chatId, creationStateKey).status === AnimationCreationStatus.CREATING,
  };
}
